import React, { useCallback, useEffect, useRef, useState } from "react";
import { limits } from "@veyso/core";
import {
  recordingErrorMessage,
  recordingStopBytes,
  startPageRecording,
  type RecordingOutcome,
  type RecordingSession,
} from "../lib/pageRecorder.js";

const MAX_MB = Math.round(limits.maxUploadBytes / (1024 * 1024));

export const clockOf = (seconds: number) => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

export type PagePhase =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "recording"; session: RecordingSession; seconds: number; bytes: number }
  | { kind: "recorded"; outcome: RecordingOutcome & { file: File }; seconds: number };

/**
 * The page recorder's state, owned by the form that shows it.
 *
 * Unmounting mid-recording cancels it, so leaving the page never leaves the
 * screen being shared with nothing listening.
 */
export function usePageRecording(onError: (message: string) => void) {
  const [phase, setPhase] = useState<PagePhase>({ kind: "idle" });
  const session = useRef<RecordingSession | null>(null);
  const lastSeconds = useRef(0);
  const startedAt = useRef(0);

  useEffect(() => () => session.current?.cancel(), []);

  // Leaving with the tab still shared loses the recording; say so first.
  useEffect(() => {
    if (phase.kind !== "recording") return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase.kind]);

  const start = useCallback(
    async (microphone: boolean) => {
      setPhase({ kind: "starting" });
      lastSeconds.current = 0;
      try {
        const started = await startPageRecording({
          microphone,
          stopBytes: recordingStopBytes(limits.maxUploadBytes),
          onProgress: ({ seconds, bytes }) => {
            lastSeconds.current = seconds;
            setPhase((current) => (current.kind === "recording" ? { ...current, seconds, bytes } : current));
          },
          onFinished: (outcome) => {
            session.current = null;
            const seconds = Math.max(lastSeconds.current, (Date.now() - startedAt.current) / 1000);
            if (outcome.file) {
              setPhase({ kind: "recorded", outcome: { ...outcome, file: outcome.file }, seconds });
              return;
            }
            setPhase({ kind: "idle" });
            if (outcome.reason === "empty") onError("Nothing was recorded. Try again, and leave it running a moment.");
          },
        });
        session.current = started;
        startedAt.current = Date.now();
        setPhase({ kind: "recording", session: started, seconds: 0, bytes: 0 });
      } catch (error) {
        setPhase({ kind: "idle" });
        const message = recordingErrorMessage(error);
        if (message) onError(message);
      }
    },
    [onError],
  );

  const reset = useCallback(() => setPhase({ kind: "idle" }), []);
  return { phase, start, reset };
}

/** The clock ticks between chunks, so the timer moves every second rather than in jumps. */
function useElapsed(running: boolean) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!running) return;
    const began = Date.now();
    setSeconds(0);
    const timer = window.setInterval(() => setSeconds((Date.now() - began) / 1000), 500);
    return () => window.clearInterval(timer);
  }, [running]);
  return seconds;
}

export const RecordingPanel: React.FC<{ session: RecordingSession; bytes: number }> = ({ session, bytes }) => {
  const preview = useRef<HTMLVideoElement>(null);
  const seconds = useElapsed(true);

  useEffect(() => {
    if (preview.current) preview.current.srcObject = session.stream;
  }, [session.stream]);

  return (
    <section className="surface flex flex-col gap-3.5 border-[rgba(220,38,38,0.35)] p-4" aria-live="polite">
      <div className="flex items-center gap-2.5">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-signal-bad" aria-hidden="true" />
        <span className="text-[12px] font-bold tracking-[0.06em] text-signal-bad">REC</span>
        <span className="tnum text-[20px] font-semibold text-ink-100">{clockOf(seconds)}</span>
        <span className="flex-1" />
        <span className="tnum text-[11.5px] text-ink-300">
          {Math.round(bytes / (1024 * 1024))} MB of {MAX_MB} MB
        </span>
      </div>
      <div className="flex flex-wrap items-start gap-3.5">
        <video
          ref={preview}
          autoPlay
          muted
          playsInline
          className="h-[124px] w-[220px] shrink-0 rounded-[6px] bg-[#1e2530] object-contain"
        />
        <div className="flex min-w-[200px] flex-1 flex-col gap-2 text-[12.5px] leading-relaxed text-ink-200">
          <p>
            Recording <b>{session.surface}</b>. Go and do the demo there, then come back and press Stop, or use the
            browser&apos;s <b>Stop sharing</b> bar.
          </p>
          <p className="text-[12px] text-ink-300">{session.microphone ? "Microphone on" : "Microphone off"}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn btn-danger" onClick={() => session.stop()}>
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24">
            <rect x="5" y="5" width="14" height="14" rx="2" fill="currentColor" />
          </svg>
          Stop
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => session.cancel()}>
          Cancel
        </button>
      </div>
    </section>
  );
};

export const RecordedPanel: React.FC<{
  file: File;
  seconds: number;
  limited: boolean;
  disabled: boolean;
  onAgain(): void;
}> = ({ file, seconds, limited, disabled, onAgain }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const made = URL.createObjectURL(file);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [file]);

  return (
    <section className="surface flex flex-col gap-3.5 border-ember-500 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#15803d"
          strokeWidth="2.5"
        >
          <path d="M5 12l5 5L19 7" />
        </svg>
        <span className="text-[13px] font-semibold text-ink-100">Recording ready</span>
        <span className="tnum text-[12px] text-ink-300">
          {clockOf(seconds)} · {(file.size / (1024 * 1024)).toFixed(1)} MB ·{" "}
          {file.type === "video/mp4" ? "MP4" : "WebM"}
        </span>
      </div>
      {limited ? (
        <p className="text-[12px] text-signal-warn">Stopped on its own at the {MAX_MB} MB upload limit.</p>
      ) : null}
      {url ? (
        <video src={url} controls playsInline className="max-h-[300px] w-full rounded-[8px] bg-[#1e2530]" />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn btn-ghost btn-sm" disabled={disabled} onClick={onAgain}>
          Record again
        </button>
        {url ? (
          <a className="btn btn-ghost btn-sm" href={url} download={file.name}>
            Save a copy
          </a>
        ) : null}
      </div>
    </section>
  );
};
